import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import QRCode from 'qrcode';
import { actualizarComercioPuntos, asociarAdminComercio, crearComercioPuntos, desasociarAdminComercio, generarQrDinamico, getComerciosResumen, listarAdminsComercio, subirLogoComercio } from '../services/puntosAdminService.tsx';
import LogoCamara from '../assets/LogoCamara.png';
import socioService from '../services/socioService.tsx';

const CAMARA_NOMBRE = 'Cámara Comercial de Bolívar';

type SocioScan = { socio_id: number; nombre: string; puntos: number; fecha_carga: string };
type AdminSocio = { socio_id: number; nombre: string; mail?: string | null };
type Comercio = {
  comercio_id: number;
  nombre: string;
  puntos_por_carga: number;
  activo: boolean;
  logo_url?: string | null;
  totalPuntos: number;
  totalScans: number;
  socios: SocioScan[];
  admins?: AdminSocio[];
};

export default function PuntosAdminPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [comercios, setComercios] = useState<Comercio[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [nombre, setNombre] = useState('');
  const [puntos, setPuntos] = useState(100);

  const [selectedComercio, setSelectedComercio] = useState<Comercio | null>(null);
  const [showSociosModal, setShowSociosModal] = useState(false);
  const [textoLibre, setTextoLibre] = useState('');
  const [datosAdicionales, setDatosAdicionales] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrPayload, setQrPayload] = useState('');
  const [qrExpiresAtMs, setQrExpiresAtMs] = useState<number | null>(null);
  const [generandoQr, setGenerandoQr] = useState(false);
  const [uploadingLogoId, setUploadingLogoId] = useState<number | null>(null);
  const [togglingActivoId, setTogglingActivoId] = useState<number | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loadingComercios, setLoadingComercios] = useState(true);
  const [showAdminsModal, setShowAdminsModal] = useState(false);
  const [adminsActuales, setAdminsActuales] = useState<AdminSocio[]>([]);
  const [guardandoAdmin, setGuardandoAdmin] = useState(false);
  const [buscarAdminTerm, setBuscarAdminTerm] = useState('');
  const [resultadosBusquedaAdmins, setResultadosBusquedaAdmins] = useState<any[]>([]);
  const [buscandoAdmins, setBuscandoAdmins] = useState(false);
  const [showEditPuntosModal, setShowEditPuntosModal] = useState(false);
  const [comercioEditPuntos, setComercioEditPuntos] = useState<Comercio | null>(null);
  const [puntosEditValue, setPuntosEditValue] = useState<number>(0);
  const [guardandoPuntosEdit, setGuardandoPuntosEdit] = useState(false);
  const [errorPuntosEdit, setErrorPuntosEdit] = useState('');

  const load = async () => {
    try {
      setLoadingComercios(true);
      const token = await getAccessTokenSilently();
      const data = await getComerciosResumen(token);
      setComercios(data as Comercio[]);
    } finally {
      setLoadingComercios(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  useEffect(() => {
    const run = async () => {
      if (!qrPayload) return setQrDataUrl('');
      setQrDataUrl(await QRCode.toDataURL(qrPayload, { width: 900, margin: 1 }));
    };
    run();
  }, [qrPayload]);

  useEffect(() => {
    if (!showPrintModal || !selectedComercio?.activo || !qrExpiresAtMs) return;
    const interval = setInterval(() => {
      const remaining = qrExpiresAtMs - Date.now();
      if (remaining <= 0 && !generandoQr) {
        generarQr(selectedComercio.comercio_id);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [showPrintModal, selectedComercio, qrExpiresAtMs, generandoQr]);

  const crear = async () => {
    const token = await getAccessTokenSilently();
    await crearComercioPuntos({ nombre, puntos_por_carga: puntos, activo: true }, token);
    setNombre('');
    setPuntos(100);
    setShowModal(false);
    await load();
  };

  const generarQr = async (comercioId?: number) => {
    const id = comercioId || selectedComercio?.comercio_id;
    if (!id) return;
    try {
      setGenerandoQr(true);
      const token = await getAccessTokenSilently();
      const resp = await generarQrDinamico(id, token, 60);
      setQrPayload(resp.token_qr || '');
      setQrExpiresAtMs(resp.expires_at ? new Date(resp.expires_at).getTime() : null);
    } finally {
      setGenerandoQr(false);
    }
  };

  const abrirPlantilla = async (c: Comercio) => {
    setSelectedComercio(c);
    setTextoLibre('');
    setDatosAdicionales('');
    setQrPayload('');
    setQrExpiresAtMs(null);
    setShowPrintModal(true);
    if (c.activo) await generarQr(c.comercio_id);
  };

  const abrirSociosModal = (c: Comercio) => {
    setSelectedComercio(c);
    setShowSociosModal(true);
  };

  const abrirAdminsModal = async (c: Comercio) => {
    setSelectedComercio(c);
    setShowAdminsModal(true);
    setBuscarAdminTerm('');
    setResultadosBusquedaAdmins([]);
    const token = await getAccessTokenSilently();
    const admins = await listarAdminsComercio(c.comercio_id, token);
    setAdminsActuales(admins);
  };

  const buscarAdmins = async () => {
    const term = buscarAdminTerm.trim();
    if (!term) {
      setResultadosBusquedaAdmins([]);
      return;
    }
    try {
      setBuscandoAdmins(true);
      const data = await socioService.searchSocios(term, getAccessTokenSilently);
      setResultadosBusquedaAdmins(Array.isArray(data) ? data.slice(0, 12) : []);
    } finally {
      setBuscandoAdmins(false);
    }
  };

  const agregarAdmin = async (socioId: number) => {
    if (!selectedComercio) return;
    if (!Number.isFinite(socioId) || socioId <= 0) return;
    try {
      setGuardandoAdmin(true);
      const token = await getAccessTokenSilently();
      await asociarAdminComercio(selectedComercio.comercio_id, socioId, token);
      const admins = await listarAdminsComercio(selectedComercio.comercio_id, token);
      setAdminsActuales(admins);
      setResultadosBusquedaAdmins((prev) => prev.filter((s: any) => Number(s?.socio_id) !== socioId));
      await load();
    } finally {
      setGuardandoAdmin(false);
    }
  };

  const quitarAdmin = async (socioId: number) => {
    if (!selectedComercio) return;
    const token = await getAccessTokenSilently();
    await desasociarAdminComercio(selectedComercio.comercio_id, socioId, token);
    const admins = await listarAdminsComercio(selectedComercio.comercio_id, token);
    setAdminsActuales(admins);
    await load();
  };

  const toggleActivo = async (c: Comercio) => {
    try {
      setTogglingActivoId(c.comercio_id);
      const token = await getAccessTokenSilently();
      await actualizarComercioPuntos(c.comercio_id, { activo: !c.activo }, token);
      await load();
      if (selectedComercio?.comercio_id === c.comercio_id) {
        setSelectedComercio({ ...c, activo: !c.activo });
      }
    } finally {
      setTogglingActivoId(null);
    }
  };

  const editarPuntosPorQr = (c: Comercio) => {
    setComercioEditPuntos(c);
    setPuntosEditValue(Number(c.puntos_por_carga || 0));
    setErrorPuntosEdit('');
    setShowEditPuntosModal(true);
  };

  const guardarPuntosPorQr = async () => {
    if (!comercioEditPuntos) return;
    const nuevo = Number(puntosEditValue);
    if (!Number.isFinite(nuevo) || nuevo <= 0) {
      setErrorPuntosEdit('Ingresá un número válido mayor a 0');
      return;
    }

    try {
      setErrorPuntosEdit('');
      setGuardandoPuntosEdit(true);
      const token = await getAccessTokenSilently();
      await actualizarComercioPuntos(
        comercioEditPuntos.comercio_id,
        { puntos_por_carga: Math.floor(nuevo) },
        token
      );
      setShowEditPuntosModal(false);
      setComercioEditPuntos(null);
      await load();
    } finally {
      setGuardandoPuntosEdit(false);
    }
  };

  const onUploadLogo = async (comercioId: number, file?: File | null) => {
    if (!file) return;
    try {
      setUploadingLogoId(comercioId);
      setShowUploadModal(true);
      const token = await getAccessTokenSilently();
      await subirLogoComercio(comercioId, file, token);
      await load();
    } finally {
      setUploadingLogoId(null);
      setShowUploadModal(false);
    }
  };

  const segundosRestantes = qrExpiresAtMs ? Math.max(0, Math.ceil((qrExpiresAtMs - Date.now()) / 1000)) : 0;

  return (
    <>
      <style>{`
        .print-sheet { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body * { visibility: hidden !important; }
          .print-sheet, .print-sheet * { visibility: visible !important; }
          .print-sheet { display: block !important; position: fixed; inset: 0; }
          .print-card { border: 2px solid #dbeafe; border-radius: 20px; padding: 18mm 14mm; background: linear-gradient(180deg,#eff6ff 0%,#fff 35%); }
          .print-header { display:flex; align-items:center; gap:14px; border-bottom:2px solid #bfdbfe; padding-bottom:10px; margin-bottom:14px; }
          .print-logo { width: 101px; height: 101px; object-fit: contain; }
          .print-title { margin: 0; font-size: 38px; font-weight: 800; color: #1e3a8a; line-height: 1.05; white-space: nowrap; }
          .print-qr-wrap { margin-top: 14px; border:2px dashed #60a5fa; border-radius:18px; padding:12px; display:flex; justify-content:center; }
          .print-qr { width: 330px; height: 330px; }
        }
      `}</style>

      <div className="mx-auto max-w-6xl space-y-4 p-4">
        <div className="rounded-xl bg-white p-4 shadow flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Puntos por comercio</h1>
            <p className="text-sm text-slate-600">ABM y métricas de escaneo por comercio.</p>
          </div>
          <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={() => setShowModal(true)}>+ Nuevo comercio</button>
        </div>

        <div className="rounded-xl bg-white p-4 shadow overflow-x-auto">
          <h2 className="mb-3 font-semibold">Comercios configurados</h2>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="py-2">Logo</th>
                <th className="py-2">Comercio</th>
                <th className="py-2">Puntos/carga</th>
                <th className="py-2">Estado QR</th>
                <th className="py-2">Total otorgado</th>
                <th className="py-2">Escaneos</th>
                <th className="py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loadingComercios
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <tr key={`sk-${idx}`} className="border-b animate-pulse">
                      <td className="py-2"><div className="h-24 w-24 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-4 w-40 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-4 w-16 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-6 w-20 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-4 w-20 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-4 w-14 rounded bg-slate-200" /></td>
                      <td className="py-2"><div className="h-8 w-64 rounded bg-slate-200" /></td>
                    </tr>
                  ))
                : comercios.map((c) => (
                    <React.Fragment key={c.comercio_id}>
                      <tr className="border-b">
                        <td className="py-2">
                          {c.logo_url ? <img src={c.logo_url} alt={`logo-${c.nombre}`} className="h-24 w-24 rounded object-cover" /> : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="py-2 font-medium">{c.nombre}</td>
                        <td className="py-2">+{c.puntos_por_carga}</td>
                        <td className="py-2">
                          <span className={`rounded px-2 py-1 text-xs font-semibold ${c.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                            {c.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="py-2 font-semibold">{c.totalPuntos || 0}</td>
                        <td className="py-2">{c.totalScans || 0}</td>
                        <td className="py-2">
                          <div className="flex gap-2 flex-wrap">
                            <label className="cursor-pointer rounded border px-2 py-1 text-xs">
                              Cambiar logo
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => onUploadLogo(c.comercio_id, e.target.files?.[0])} disabled={uploadingLogoId === c.comercio_id} />
                            </label>
                            <button className="rounded bg-slate-800 px-2 py-1 text-white" onClick={() => abrirPlantilla(c)}>Plantilla A4</button>
                            <button
                              className={`rounded px-2 py-1 text-white ${c.activo ? 'bg-amber-600' : 'bg-emerald-600'}`}
                              onClick={() => toggleActivo(c)}
                              disabled={togglingActivoId === c.comercio_id}
                            >
                              {togglingActivoId === c.comercio_id ? 'Modificando QR...' : c.activo ? 'Desactivar QR' : 'Activar QR'}
                            </button>
                            <button className="rounded border px-2 py-1" onClick={() => editarPuntosPorQr(c)}>
                              Editar puntos QR
                            </button>
                            <button className="rounded border px-2 py-1" onClick={() => abrirSociosModal(c)}>
                              Ver socios escaneados
                            </button>
                            <button className="rounded border px-2 py-1" onClick={() => abrirAdminsModal(c)}>
                              Admins ({c.admins?.length || 0})
                            </button>
                          </div>
                        </td>
                      </tr>

                    </React.Fragment>
                  ))}
              {!loadingComercios && comercios.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center text-slate-500">Sin comercios.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdminsModal && selectedComercio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Admins del comercio · {selectedComercio.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowAdminsModal(false)}>Cerrar</button>
            </div>

            <div className="space-y-2 rounded border p-3">
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded border p-2"
                  placeholder="Buscar socio por nombre, mail o DNI"
                  value={buscarAdminTerm}
                  onChange={(e) => setBuscarAdminTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') buscarAdmins(); }}
                />
                <button className="rounded border px-3 py-2" onClick={buscarAdmins} disabled={buscandoAdmins}>
                  {buscandoAdmins ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              {resultadosBusquedaAdmins.length > 0 && (
                <div className="max-h-64 overflow-auto rounded border">
                  {resultadosBusquedaAdmins.map((s: any) => {
                    const socioId = Number(s.socio_id);
                    const yaAsociado = adminsActuales.some((a) => Number(a.socio_id) === socioId);
                    return (
                      <div
                        key={s.socio_id}
                        className="flex items-center justify-between border-b px-3 py-2 hover:bg-slate-50"
                      >
                        <div>
                          <p className="font-medium">{s.socio_nombre} {s.socio_apellido}</p>
                          <p className="text-xs text-slate-500">#{s.socio_id}</p>
                        </div>
                        <button
                          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-300"
                          onClick={() => agregarAdmin(socioId)}
                          disabled={guardandoAdmin || yaAsociado}
                        >
                          {yaAsociado ? 'Asociado' : (guardandoAdmin ? 'Asociando...' : 'Asociar')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded border">
              {adminsActuales.length === 0 ? (
                <p className="p-3 text-sm text-slate-500">Este comercio no tiene socios administradores asociados.</p>
              ) : (
                <ul className="divide-y">
                  {adminsActuales.map((a) => (
                    <li key={a.socio_id} className="flex items-center justify-between px-3 py-2">
                      <div>
                        <p className="font-medium">{a.nombre}</p>
                        <p className="text-xs text-slate-500">Socio #{a.socio_id}{a.mail ? ` · ${a.mail}` : ''}</p>
                      </div>
                      <button className="rounded border px-2 py-1 text-xs" onClick={() => quitarAdmin(a.socio_id)}>Quitar</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showSociosModal && selectedComercio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-xl bg-white p-4 shadow-xl space-y-3 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Socios que escanearon · {selectedComercio.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowSociosModal(false)}>Cerrar</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
              <div className="rounded bg-slate-50 p-2"><b>Comercio ID:</b> {selectedComercio.comercio_id}</div>
              <div className="rounded bg-slate-50 p-2"><b>Estado:</b> {selectedComercio.activo ? 'Activo' : 'Inactivo'}</div>
              <div className="rounded bg-slate-50 p-2"><b>Escaneos:</b> {selectedComercio.totalScans || 0}</div>
              <div className="rounded bg-slate-50 p-2"><b>Puntos otorgados:</b> {selectedComercio.totalPuntos || 0}</div>
            </div>

            {selectedComercio.socios?.length ? (
              <table className="min-w-full text-sm border rounded overflow-hidden">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">Socio ID</th>
                    <th className="px-2 py-2 text-left">Nombre</th>
                    <th className="px-2 py-2 text-left">Puntos cargados</th>
                    <th className="px-2 py-2 text-left">Fecha/Hora</th>
                    <th className="px-2 py-2 text-left">Info</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedComercio.socios.map((s, idx) => (
                    <tr key={`${s.socio_id}-${idx}`} className="border-t">
                      <td className="px-2 py-2">{idx + 1}</td>
                      <td className="px-2 py-2">{s.socio_id}</td>
                      <td className="px-2 py-2 font-medium">{s.nombre || `Socio ${s.socio_id}`}</td>
                      <td className="px-2 py-2">+{s.puntos}</td>
                      <td className="px-2 py-2">{String(s.fecha_carga || '').slice(0, 19).replace('T', ' ') || '—'}</td>
                      <td className="px-2 py-2 text-xs text-slate-500">Carga QR comercio #{selectedComercio.comercio_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-500">Sin escaneos registrados para este comercio.</p>
            )}
          </div>
        </div>
      )}

      {showPrintModal && selectedComercio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Plantilla A4 · {selectedComercio.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowPrintModal(false)}>Cerrar</button>
            </div>
            <input className="w-full rounded border p-2" placeholder="Título adicional (opcional)" value={textoLibre} onChange={(e) => setTextoLibre(e.target.value)} />
            <textarea className="w-full rounded border p-2" rows={3} placeholder="Datos adicionales para imprimir (opcional)" value={datosAdicionales} onChange={(e) => setDatosAdicionales(e.target.value)} />
            <div className="flex justify-center rounded border border-sky-200 bg-sky-50 p-3">
              <div className="flex h-64 w-64 items-center justify-center rounded bg-white p-2">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR dinámico comercio" className="h-full w-full rounded object-contain" />
                ) : selectedComercio.activo ? (
                  <div className="h-full w-full animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="px-4 text-center text-sm text-slate-500">Comercio inactivo</p>
                )}
              </div>
            </div>
            {!selectedComercio.activo && (
              <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">Este comercio está inactivo. Activá el QR para poder generar uno dinámico.</p>
            )}
            {selectedComercio.activo && (
              <div className="rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-800">
                QR dinámico vigente por <b>{segundosRestantes}s</b>. Se renueva automáticamente al vencer.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowPrintModal(false)}>Cerrar</button>
              <button className="rounded bg-sky-700 px-3 py-2 text-white disabled:opacity-50" onClick={() => generarQr()} disabled={!selectedComercio.activo || generandoQr}>
                {generandoQr ? 'Generando...' : 'Regenerar QR'}
              </button>
              <button className="rounded bg-indigo-700 px-3 py-2 text-white disabled:opacity-50" onClick={() => window.print()} disabled={!qrDataUrl}>Imprimir plantilla A4</button>
            </div>
          </div>
        </div>
      )}

      {showEditPuntosModal && comercioEditPuntos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Editar puntos por QR</h3>
              <button
                className="rounded border px-3 py-1"
                onClick={() => {
                  setShowEditPuntosModal(false);
                  setComercioEditPuntos(null);
                  setErrorPuntosEdit('');
                }}
              >
                Cerrar
              </button>
            </div>
            <p className="text-sm text-slate-600">Comercio: <b>{comercioEditPuntos.nombre}</b></p>
            <label className="block text-sm text-slate-700">
              Puntos por carga
              <input
                type="number"
                min={1}
                className="mt-1 w-full rounded border p-2"
                value={puntosEditValue}
                onChange={(e) => setPuntosEditValue(Number(e.target.value || 0))}
              />
            </label>
            {errorPuntosEdit && (
              <p className="rounded bg-rose-50 px-3 py-2 text-xs text-rose-700">{errorPuntosEdit}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="rounded border px-3 py-2"
                onClick={() => {
                  setShowEditPuntosModal(false);
                  setComercioEditPuntos(null);
                  setErrorPuntosEdit('');
                }}
              >
                Cancelar
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-60"
                onClick={guardarPuntosPorQr}
                disabled={guardandoPuntosEdit}
              >
                {guardandoPuntosEdit ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl text-center">
            <p className="text-base font-semibold text-slate-900">Subiendo logo del comercio...</p>
            <p className="mt-1 text-sm text-slate-600">Esperá un momento, por favor.</p>
            <div className="mx-auto mt-4 h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
          </div>
        </div>
      )}


      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl space-y-3">
            <h3 className="text-lg font-semibold">Nuevo comercio</h3>
            <input className="w-full rounded border p-2" placeholder="Nombre comercio" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            <input className="w-full rounded border p-2" type="number" placeholder="Puntos por carga" value={puntos} onChange={(e) => setPuntos(Number(e.target.value || 0))} />
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={crear}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      <div className="print-sheet">
        <div className="print-card">
          <div className="print-header">
            <img src={LogoCamara} alt="Logo cámara" className="print-logo" />
            <div>
              <h1 className="print-title">{CAMARA_NOMBRE}</h1>
            </div>
          </div>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
            {selectedComercio?.logo_url ? (
              <img
                src={selectedComercio.logo_url}
                alt={`logo-${selectedComercio.nombre}`}
                style={{ width: 152, height: 152, objectFit: 'cover', borderRadius: 10, border: '1px solid #cbd5e1' }}
              />
            ) : null}
            <div>
              <p style={{ fontSize: 33, fontWeight: 800, color: '#0f172a', margin: 0, textAlign: 'center' }}>
                {selectedComercio?.nombre || 'Comercio'}
              </p>
              <p style={{ fontSize: 18, color: '#334155', margin: '6px 0 0', textAlign: 'center' }}>
                {textoLibre || 'Escaneá este QR para cargar tus puntos'}
              </p>
              {datosAdicionales ? (
                <p style={{ fontSize: 14, color: '#334155', margin: '8px 0 0', textAlign: 'center', whiteSpace: 'pre-line' }}>
                  {datosAdicionales}
                </p>
              ) : null}
            </div>
          </div>
          <div className="print-qr-wrap">{qrDataUrl ? <img src={qrDataUrl} alt="QR puntos" className="print-qr" /> : <p>QR inactivo</p>}</div>
          <p style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
            QR dinámico de un solo uso / vigencia corta
          </p>
        </div>
      </div>
    </>
  );
}
